<template>
  <div class="w-full max-w-[440px] mx-auto mt-[55px]">
    <div
      class="font-primary font-medium text-[28px] leading-[30px] text-gray-800 mb-[52px]"
    >
      Confirm your plan change
    </div>

    <div
      v-if="oldPlan"
      class="border border-gray-300 rounded-md pt-[13px] pl-[21px] pr-[15px] pb-[18px]"
    >
      <div class="mb-[3px] flex items-center justify-between">
        <div
          class="font-semibold font-primary text-[19px] leading-[26px] text-gray-800 opacity-50"
        >
          {{ oldPlan.name }} plan
        </div>
        <Pill>Old</Pill>
      </div>
      <div
        class="font-primary text-[14px] leading-[20px] text-gray-800 opacity-50"
      >
        <div>Billed {{ oldPlanCycle }}</div>
        <div class="flex items-center">
          <div
            v-for="(price, priceIndex) in oldPlanPrices"
            :key="`price-${priceIndex}`"
            class="flex items-center"
          >
            <span class="mr-1">{{ formatPriceLocal(price) }}</span>
            <span v-if="!priceIndex">
              <span>{{ price.details.suffix }}</span>
              <span v-if="oldPlanPrices.length > 1" class="mr-1"> + </span>
            </span>
            <span v-else>{{ price.details.suffix }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full flex justify-center -mt-px mb-[15px]">
      <Icon name="ArrowUp" :size="26" class="rotate-180 stroke-gray-300" />
    </div>

    <div
      v-if="newPlan"
      class="bg-green-200/15 border border-green-800 rounded-md pt-[13px] pl-[21px] pr-[15px] pb-[18px] mb-[11px]"
    >
      <div class="mb-[3px] flex items-center justify-between">
        <div
          class="font-semibold font-primary text-[19px] leading-[26px] text-gray-800"
        >
          {{ newPlan.name }} plan
        </div>
        <Pill success>New</Pill>
      </div>
      <div
        v-if="newPlanItem?.maxUnits"
        class="font-primary text-[14px] leading-[20px] text-gray-800 mb-[3px]"
      >
        {{ newPlanItem?.maxUnits }} copyrights
      </div>
      <div class="font-primary text-[14px] leading-[20px] text-gray-800">
        <div>Billed {{ newPlanCycle }}</div>

        <div v-if="newPlanItem" class="flex items-center">
          <div
            v-for="(price, priceIndex) in newPlanItem.prices"
            :key="`price-${priceIndex}`"
            class="flex items-center"
          >
            <span class="mr-0.5">{{ formatPriceLocal(price) }}</span>
            <span v-if="!priceIndex">
              <span>/{{ price.details?.billingCycleType }}</span>
              <span v-if="newPlanItem.prices.length > 1" class="mr-1"> + </span>
            </span>
            <span v-else>/copyright</span>
          </div>
        </div>
      </div>
    </div>

    <Message type="warning" class="mb-9">
      You will be billed immediately and your new plan will take effect upon
      completing your purchase.
    </Message>

    <RouterLink to="/change-plans/checkout">
      <UiButton primary class="w-full!">Confirm and purchase</UiButton>
    </RouterLink>
  </div>
</template>

<script setup lang="ts">
import {
  PlanCycle,
  PlanPriceType,
  type BillingPrice,
  type PlanPriceItem,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { formatPrice } from '@/shared/utils/money'
import { Icon, Pill, UiButton, Message } from '@dacr/ui'
import { computed } from 'vue'

const billingStore = useBillingStore()

const oldPlan = computed(() => billingStore.billing?.plan)
const oldPlanCycle = computed(() => {
  if (oldPlan.value?.subscriptionCycle === PlanCycle.Monthly) return 'monthly'
  return 'annually'
})
const oldPlanPrices = computed(
  () => oldPlan.value?.prices.filter((price) => price.details.amount) || []
)

const newPlan = computed(() => billingStore.selectedPlan)
const newPlanItem = computed(() => billingStore.selectedPlanItem)
const newPlanCycle = computed(() => {
  if (
    newPlanItem.value?.prices.find(
      (price) => price.type === PlanPriceType.Subscription
    )?.details?.billingCycleType === 'year'
  ) {
    return 'annually'
  }
  return 'monthly'
})

const formatPriceLocal = (price: BillingPrice | PlanPriceItem) => {
  return formatPrice({
    amount: (price.details?.amount || 0) / 100,
    currency: price.details?.currency,
  })
}
</script>
